/**
 * @file PlanNoticeDialog.tsx
 * @brief One calm dialog when this account's plan changed (upgrade,
 *        downgrade, a cancel taking effect, a failed payment, a trial ending).
 *        Web and the Mac app; never the phone (ADR-043).
 *
 * @details
 * Plans-visible brief C (21-10-2026). On sign-in or app open, GET
 * /billing/plan-notice returns the newest unseen change (BACKEND Part 1); if
 * there is one: "You're now on …", what you can still do, what you can't
 * (only real limits), and a primary button (View plans for personal users
 * and company admins, OK for members). Closing marks it seen (POST
 * /billing/plan-notice/seen), so it never shows twice. A backend without the
 * route answers 404: nothing shows.
 *
 * @author DES
 * @date 21-10-2026
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CircleCheck, CircleX, ListTodo, Plug, X } from "lucide-react";
import { z } from "zod";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/ui/InfoTip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogClose,
  DialogTitle,
} from "@/components/ui/Dialog";
import { apiGet, apiPost } from "@/shared/api/client";
import { billingEndpoints } from "@/shared/api/endpoints";
import { useAuth } from "@/shared/auth/auth-context";
import { useMySubscription, usePlanCatalog } from "@/features/my-billing/api";
import { billingAudienceFor } from "@/features/my-billing/billingErrors";
import { formatDay } from "@/features/my-billing/components/format";
import { planNoticeCopy } from "./planNoticeCopy";
import { planTips } from "./planTips";
import { cn } from "@/shared/theme/cn";

const NoticeSchema = z
  .object({
    id: z.union([z.string(), z.number()]).optional(),
    from: z.string().nullable().optional(),
    to: z.string(),
    /** upgrade | downgrade | ended | trial_ended | payment_failed (ADR-049). */
    kind: z.string().nullable().optional(),
    effectiveAt: z.string().nullable().optional(),
  })
  .passthrough();
const ReplySchema = z.object({ notice: NoticeSchema.nullable().optional() }).passthrough();
type Notice = z.infer<typeof NoticeSchema>;

/**
 * Dev only: `?planNotice=business` (or pro, free, past_due…) shows the dialog
 * with a made-up notice, to look at it in the browser or the Mac dev app.
 * Nothing is read or marked seen on the server.
 */
function previewNotice(): Notice | null {
  if (!import.meta.env.DEV || typeof window === "undefined") return null;
  const to = new URLSearchParams(window.location.search).get("planNotice");
  if (!to) return null;
  return {
    id: "preview",
    to: to === "past_due" ? "business" : to,
    kind: to === "past_due" ? "payment_failed" : to === "free" ? "ended" : "upgrade",
    effectiveAt: new Date().toISOString(),
  };
}

export function PlanNoticeDialog() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Wait for the Terms gate: an open modal dialog takes every click, so the
  // gate's "Agree and continue" could not be pressed (21-10-2026). The
  // notice shows once the terms are agreed.
  const enabled = !!user && user.role !== "superadmin" && user.needsTermsAcceptance !== true;
  const preview = previewNotice();
  const notice = useQuery<Notice | null>({
    queryKey: ["billing", "plan-notice", user?.id, preview?.to ?? null],
    enabled,
    staleTime: Infinity,
    retry: false,
    queryFn: async () => {
      if (preview) return preview;
      try {
        const raw = await apiGet<unknown>(billingEndpoints.planNotice);
        // The notice itself, or wrapped as { notice }.
        const direct = NoticeSchema.safeParse(raw);
        if (direct.success) return direct.data;
        return ReplySchema.parse(raw).notice ?? null;
      } catch {
        return null; // older backend (404) or offline: nothing to say
      }
    },
  });
  const status = useMySubscription();
  const catalog = usePlanCatalog();
  const [closed, setClosed] = useState(false);
  const n = notice.data;
  if (!enabled || !n || closed) return null;

  const audience = billingAudienceFor(user);
  const copy = planNoticeCopy(n.kind === "payment_failed" ? "past_due" : n.to, {
    catalog: status.data?.catalog ?? [],
    free: status.data?.freePlan ?? null,
    audience,
  });
  // A failed payment keeps the plan: say that, not "You're now on Business".
  const member = audience === "company_member";
  const tips = planTips(catalog.data?.compare ?? catalog.data?.catalog ?? status.data?.catalog ?? []);
  const tipFor = (line: string) => {
    const key = copy.tips[line];
    return key ? <InfoTip label={line} text={tips[key]} className="ml-1" /> : null;
  };
  const failed = n.kind === "payment_failed";
  // Our own words, by what happened (DES 21-10-2026: not the reference's).
  const who = audience === "personal" ? "your account" : "your whole company";
  const toCode = n.to.toLowerCase();
  const toFree = toCode === "free" || toCode === "basic" || toCode === "none";
  const pill = failed
    ? "Payment needed"
    : toFree
      ? "Plan updated"
      : copy.planName
        ? `Welcome to ${copy.planName}`
        : null;
  const sentence = failed
    ? "We couldn't take your last payment."
    : toFree
      ? `${audience === "personal" ? "Your account" : "Your company"} is on Free from today.`
      : `${copy.planName} is now active for ${who}.`;
  const plansHref = audience === "company_admin" ? "/company/billing" : "/app/billing/plan";

  const close = (go?: string) => {
    setClosed(true);
    if (n.id !== "preview") void apiPost(billingEndpoints.planNoticeSeen, n.id != null ? { id: n.id } : {}).catch(() => {});
    if (go) navigate(go);
  };

  const lines = (items: string[], muted: boolean) => (
    <ul className="space-y-3">
      {items.map((line) => {
        const Icon = muted ? CircleX : CircleCheck;
        return (
          <li key={line} className="flex items-start gap-3 text-[15px] leading-snug">
            <Icon
              className={cn("mt-px size-5 shrink-0", muted ? "text-muted-foreground" : "text-brand")}
              strokeWidth={1.75}
            />
            <span>
              {line}
              {tipFor(line)}
            </span>
          </li>
        );
      })}
    </ul>
  );

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent
        hideClose
        className="max-w-[440px] gap-0 rounded-3xl p-8 outline-none focus:outline-none focus-visible:outline-none sm:rounded-3xl"
        // Focus the dialog itself: focusing the first (i) would open its tip
        // over the list, and a focused button shows its ring (21-10-2026).
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus();
        }}
      >
        <DialogClose
          aria-label="Close"
          className={cn(
            "absolute right-5 top-5 flex size-11 items-center justify-center rounded-full ring-1 transition-colors",
            "text-muted-foreground ring-brand/40 hover:bg-brand/10 hover:text-foreground focus-visible:outline-none focus-visible:ring-2",
          )}
        >
          <X className="size-5" />
        </DialogClose>
        {pill ? (
          <span
            className={cn(
              "mr-14 w-fit rounded-full px-3.5 py-1.5 text-sm font-medium",
              // The app's soft pair: light text on dark, dark text on light, so
              // red on red stays readable (21-10-2026).
              failed ? "bg-destructive/15 text-destructive dark:text-red-200" : "bg-brand-soft text-brand-soft-foreground",
            )}
          >
            {pill}
          </span>
        ) : null}
        <DialogTitle className="mt-5 text-[28px] font-semibold leading-[1.2] tracking-tight">
          {sentence}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {n.effectiveAt ? `${member ? "Your company's plan changed" : "Your plan changed"} on ${formatDay(n.effectiveAt)}.` : copy.title}
        </DialogDescription>
        {copy.can.length ? (
          <section className="mt-7">
            <p className="mb-3 text-[15px] text-muted-foreground">{copy.canHeading}</p>
            {lines(copy.can, false)}
          </section>
        ) : null}
        {copy.cannot.length ? (
          <section className="mt-6">
            <p className="mb-3 text-[15px] text-muted-foreground">You can't</p>
            {lines(copy.cannot, true)}
          </section>
        ) : null}
        {copy.apps.length ? (
          <section className="mt-6 border-t border-border/70 pt-6">
            <p className="mb-3 text-[15px] text-muted-foreground">Connected apps</p>
            <ul className="space-y-3">
              {copy.apps.map((line) => {
                const Icon = copy.tips[line] === "taskApps" ? ListTodo : Plug;
                return (
                  <li key={line} className="flex items-start gap-3 text-[15px] leading-snug">
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-md bg-foreground/[0.07] text-foreground">
                      <Icon className="size-3.5" strokeWidth={2} />
                    </span>
                    <span>
                      {line}
                      {tipFor(line)}
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ) : null}
        <Button
          size="lg"
          className="mt-8 h-12 w-full rounded-full text-base"
          onClick={() => (member ? close() : close(plansHref))}
        >
          {member ? "Got it" : "View plans"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
